import { supabase } from '../lib/supabase';

export interface GiftCardEmailSettings {
  id?: string;
  business_id: string;
  
  // Email Header Text
  email_header_recipient: string;
  email_header_purchaser: string;
  
  // Greeting Text
  email_greeting_recipient: string;
  email_greeting_purchaser: string;
  
  // Main Message Text
  email_message_recipient: string;
  email_message_purchaser: string;
  
  // Details Section
  email_details_title: string;
  email_code_label: string;
  email_amount_label: string;
  
  // Instructions Section
  email_instructions_title: string;
  email_instruction_1: string;
  email_instruction_2: string;
  email_instruction_3: string;
  email_instruction_4: string;
  
  // Footer Text
  email_footer_text: string;
  email_footer_subtext: string;
  
  // Typography
  font_family: 'sans-serif' | 'serif' | 'monospace' | 'cursive';
  
  // Timestamps
  created_at?: string;
  updated_at?: string;
}

export const defaultGiftCardEmailSettings: GiftCardEmailSettings = {
  business_id: '',
  email_header_recipient: 'You\'ve Received a Gift Card!',
  email_header_purchaser: 'Your Gift Card is Ready!',
  email_greeting_recipient: 'Hi {recipient_name},',
  email_greeting_purchaser: 'Hello,',
  email_message_recipient: 'You\'ve received a gift card from {business_name}!',
  email_message_purchaser: 'Your gift card from {business_name} has been created successfully.',
  email_details_title: 'Gift Card Details',
  email_code_label: 'Gift Card Code:',
  email_amount_label: 'Amount:',
  email_instructions_title: 'How to Use Your Gift Card:',
  email_instruction_1: 'Present this gift card code when making a purchase: {gift_card_code}',
  email_instruction_2: 'The gift card has a balance of {amount}',
  email_instruction_3: 'You can use it for any service or product at {business_name}',
  email_instruction_4: 'Save this email for your records',
  email_footer_text: 'Thank you for choosing {business_name}!',
  email_footer_subtext: 'If you have any questions, please contact us.',
  font_family: 'sans-serif'
};

export class GiftCardEmailSettingsService {
  /**
   * Get email settings for a business
   */
  static async getEmailSettings(businessId: string): Promise<GiftCardEmailSettings | null> {
    try {
      if (!supabase) {
        console.error('Supabase client not initialized');
        return null;
      }

      const { data, error } = await supabase
        .from('gift_card_email_settings')
        .select('*')
        .eq('business_id', businessId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No settings found, return defaults
          console.log('No email settings found for business:', businessId);
          return {
            ...defaultGiftCardEmailSettings,
            business_id: businessId
          };
        }
        throw error;
      }

      return data as GiftCardEmailSettings;
    } catch (error) {
      console.error('Error fetching email settings:', error);
      return {
        ...defaultGiftCardEmailSettings,
        business_id: businessId
      };
    }
  }

  /**
   * Save email settings for a business
   */
  static async saveEmailSettings(businessId: string, settings: Partial<GiftCardEmailSettings>): Promise<boolean> {
    try {
      if (!supabase) {
        console.error('Supabase client not initialized');
        return false;
      }

      // Check if settings exist
      const { data: existing } = await supabase
        .from('gift_card_email_settings')
        .select('id')
        .eq('business_id', businessId)
        .single();

      if (existing) {
        // Update existing settings
        const { error } = await supabase
          .from('gift_card_email_settings')
          .update({
            ...settings,
            updated_at: new Date().toISOString()
          })
          .eq('business_id', businessId);

        if (error) throw error;
      } else {
        // Create new settings
        const { error } = await supabase
          .from('gift_card_email_settings')
          .insert({
            business_id: businessId,
            ...defaultGiftCardEmailSettings,
            ...settings,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });

        if (error) throw error;
      }

      return true;
    } catch (error) {
      console.error('Error saving email settings:', error);
      return false;
    }
  }
}

